import type { InitialConfig, ItemsResponse } from '@src/types';
import { resizeVtexImage, sortSizes } from '@core/utils';
import { metaAdapter } from '@meta/index';
import { normalizeBrand } from '@src/core/farm';
import { getItemCategory, getItemCategory2 } from './utils';
import type {
  ReservaProduct,
  ReservaBrandContext,
  ReservaItemsContext,
  ReservaValueContext,
  ReservaLineItemsContext,
  ReservaAvailableGridContext,
} from "./types";


export const reservaAdapter = {
  ...metaAdapter, // global adapters

  items: (context: ReservaItemsContext): ItemsResponse[] | null => {
    if (context?.items) return context.items

    if (context?.products) {
      return context.products.map((item: ReservaProduct) => {
        const COLOR_REGEX = /^[^_]+_/;
        const listPrice = item?.offers?.offers?.[0].listPrice
        const refIdValue = (item.additionalProperty?.find((i) => i.name === "RefId")?.value as string)?.replace(/_\d+$/, "");
        return {
          quantity: 1,
          index: item?.index ?? 0,
          item_brand: item.brand?.name?.toLowerCase() ?? null,
          item_name: item.name ?? null,
          price: item.offers?.lowPrice ?? null,
          discount: Math.round((Number(listPrice) - Number(item?.offers?.lowPrice)) * 100) / 100,
          item_variant: item?.name?.split(' - ')[0]?.replace(COLOR_REGEX, '').replace(/_/g, ' ') || null, // cor
          item_variant2: item?.name?.split(' - ')[1] ?? null, // tamanho
          item_shipping_tier: '',
          item_ref: refIdValue ?? null,
          item_category: getItemCategory(item) ?? null,
          item_category2: getItemCategory2(item) ?? null,
          item_id: item?.id ?? null,
          item_sku: item?.sku ?? null,
          item_url: item?.seo?.canonical ?? null,
          image_url: resizeVtexImage(item?.image?.[0].url) ?? null,
          seller_id: item.offers?.offers?.[0]?.seller?.identifier ?? null,
          item_list_name: item?.item_list_name ?? null,
        }
      })
    }

    return null
  },

  brand: (context: ReservaBrandContext, config: InitialConfig) => {
    if (config?.brand) return config.brand
    if (context?.brand) return context.brand
    if (context?.products) {
      return normalizeBrand(context.products.map((p) => p?.brand?.name))
    }
    return null
  },

  line_items: (context: ReservaLineItemsContext): string | null => {
    if (context?.line_items) return context.line_items;

    if (context?.products) {
      return context.products
        .map((item: ReservaProduct) => item?.id)
        .filter((id): id is string => Boolean(id))
        .join()
    }

    return null;
  },

  value: (context: ReservaValueContext): number | null => {
    if (context?.value) return context.value

    if (context?.products) {
      return context.products
        .map((item: ReservaProduct) => item.offers?.lowPrice)
        .filter((price): price is number => price !== undefined)
        .reduce((sum, price) => sum + price, 0)
    }

    return null
  },

  available_grid: (context: ReservaAvailableGridContext): string | null => {
    if (context?.available_grid) return context.available_grid

    if (context?.products) {
      const sizes = context.products
        .filter((product: ReservaProduct) => product.offers?.offers?.some((o) => o.availability === 'https://schema.org/InStock'))
        .map((product) => product.name?.split(' - ')[1])
        .filter((name): name is string => Boolean(name))
      const result = sortSizes(sizes).join(",").trim()

      return result || null
    }

    return null;
  },
}